import React, { useCallback } from "react";
import {
  Link,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { ArticleDto } from "@lib/ShiOneClient";
import { fromNow } from "shared-ui/src/functions/convertDate";
import { DateTime } from "luxon";
import Logo, { LOGO_CONSTANTS } from "@components/Logo";
import { IsNewsLink } from "@features/news-center-feature/utils/newsCenterUtils";
import { MANUFACTURER_LOGO_SIZE_MAP } from "shared-ui/src/utils/manufacturerLogoUtils";

interface NewsItemProps {
  article: ArticleDto;
  onClick: (article: ArticleDto) => void;
}

const LOGO_SIZE = MANUFACTURER_LOGO_SIZE_MAP["large"];

export default function NewsItem({
  article,
  onClick,
}: Readonly<NewsItemProps>) {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (IsNewsLink(article)) {
        e.preventDefault();
        window.open(article.sourceUrl, "_blank", "noopener,noreferrer");
      }
      onClick(article);
    },
    [article, onClick],
  );

  const getPartnerLogo = () => {
    const partner = article.partner || "SHI";
    const hasLogo = partner in LOGO_CONSTANTS;
    return (
      <Logo
        product={hasLogo ? partner : "SHI"}
        shape="square"
        customSize={LOGO_SIZE}
      />
    );
  };

  const getTimeAgo = () => {
    if (!article.modified) return "";
    const timestamp = fromNow(DateTime.fromJSDate(article.modified));
    if (!timestamp) return "";
    return ` - ${timestamp}`;
  };

  return (
    <ListItem divider disablePadding>
      <ListItemButton
        onClick={handleClick}
        sx={{
          px: 2,
          py: 1,
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <ListItemAvatar>{getPartnerLogo()}</ListItemAvatar>
        <ListItemText
          primary={
            <Link component="span" underline="none" variant="body1">
              {article.title}
            </Link>
          }
          secondary={(article.partner || "SHI News") + getTimeAgo()}
          slotProps={{
            secondary: {
              variant: "body2",
              color: "text.secondary",
            },
          }}
        />
      </ListItemButton>
    </ListItem>
  );
}
